import { ActionAfterViewObserver } from '@cian/react-utils';
import { Body1, Tooltip, UIHeading1 } from '@cian/ui-kit';
import { IconFunctionalInformationCircle16 } from '@cian/ui-kit-design-tokens/icons';

import { clsx } from 'clsx';
import { type FC, type JSX, type ReactNode } from 'react';

import { DATA_AS_NAV_TAB_ATTR } from '../Header/internal/utils/dataAsNavTabAttr';
import { Layout } from '../Layout';

import styles from './LayoutBlock.css';

type TSemiTitle = {
  title: string;
  hint?: ReactNode;
};

type TContentOffset = 24;
type TOffset = 90 | 74 | 60;

type LayoutBlockProps = {
  rightAdornment?: ReactNode;
  sectionTabTitle?: string;
  title?: string | JSX.Element;
  semiTitle?: TSemiTitle;
  description?: string;
  id: string;
  children: ReactNode | ReactNode[];
  contentOffset?: TContentOffset;
  topOffset?: TOffset;
  bottomOffset?: TOffset;
  backgroundTheme?: 'black' | 'white';
  onView: () => void;
};

export const LayoutBlock: FC<LayoutBlockProps> = ({
  title,
  id,
  semiTitle,
  children,
  contentOffset,
  onView,
  sectionTabTitle,
  description,
  rightAdornment,
  backgroundTheme = 'white',
  topOffset = 90,
  bottomOffset,
}) => {
  const tabAttributes = sectionTabTitle ? { [DATA_AS_NAV_TAB_ATTR]: sectionTabTitle } : null;

  return (
    <ActionAfterViewObserver
      onInViewport={({ subscribeState }) => {
        subscribeState.setSubscribeUnavailable();
        onView();
      }}
    >
      {({ elementRef }) => (
        <section
          id={id}
          {...tabAttributes}
          className={clsx(styles['section'], backgroundTheme && styles[`section-theme--${backgroundTheme}`])}
          style={{
            marginTop: backgroundTheme === 'black' ? topOffset : undefined,
          }}
          ref={elementRef}
        >
          <Layout>
            <div className={styles['container']} style={{ paddingBottom: bottomOffset, paddingTop: topOffset }}>
              {title && (
                <div data-testid={`${id}-header`}>
                  <div className={styles['title-container']} data-testid={`${id}-header--title-container`}>
                    <UIHeading1 color="current_color" data-testid={`${id}-header--title`}>
                      {title}
                    </UIHeading1>
                    {rightAdornment && (
                      <div className={styles['right-adornment']} data-testid={`${id}-header--title-right-adornment`}>
                        {rightAdornment}
                      </div>
                    )}
                  </div>
                  {semiTitle && (
                    <div className={styles['semi-title']} data-testid={`${id}-header--semi-title-container`}>
                      <Body1 data-testid={`${id}-header--semi-title`} display="inline">
                        {semiTitle.title}
                      </Body1>
                      {semiTitle.hint && (
                        <Tooltip
                          theme="white"
                          title={<div data-testid={`${id}-header--semi-title-hint-tooltip`}>{semiTitle.hint}</div>}
                        >
                          <div className={styles['hint-icon']} data-testid={`${id}-header--semi-title-hint-icon`}>
                            <IconFunctionalInformationCircle16 />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {description && (
                    <div className={styles['description']} data-testid={`${id}-header--description`}>
                      <Body1>{description}</Body1>
                    </div>
                  )}
                </div>
              )}
              <div
                className={clsx(styles['content'], contentOffset && styles[`content-offset--${contentOffset}`])}
                data-testid={`${id}-content`}
              >
                {children}
              </div>
            </div>
          </Layout>
        </section>
      )}
    </ActionAfterViewObserver>
  );
};
